/**
 * Design Tokens — JavaScript accessible version.
 * Single source of truth for JS/React code (mirrors design-tokens.scss).
 *
 * Source: Figma "❖ Dashboard – Design System (2025)"
 * File Key: YRuXde1NJPrgi0JWFLX5ua
 *
 * Usage:
 *   import { tokens } from 'assets/theme'        // raw values
 *   import { antdTheme } from 'assets/theme'     // ConfigProvider theme
 */

// ============================================================
// RAW DESIGN TOKENS
// ============================================================

export const colors = {
  // Brand
  primary: '#6852d6',
  primary25: '#fafaff',
  primary50: '#f4f3ff',
  primary100: '#ebe9fe',
  primary200: '#d9d6fe',
  primary300: '#bdb4fe',
  primary400: '#9b8afb',
  primary500: '#7b70e4',
  primary600: '#6852d6',
  primary700: '#5925dc',
  primary800: '#4a1fb8',
  primary900: '#3e1c96',

  // Neutral
  white: '#ffffff',
  black: '#0a0d12',
  neutral50: '#fafafa',
  neutral100: '#f5f5f5',
  neutral200: '#e9eaeb',
  neutral300: '#d5d7da',
  neutral400: '#a4a7ae',
  neutral500: '#717680',
  neutral600: '#535862',
  neutral700: '#414651',
  neutral800: '#252b37',
  neutral900: '#181d27',
  neutral950: '#0a0d12',

  // Error
  error50: '#fef3f2',
  error100: '#fee4e2',
  error500: '#f04438',
  error600: '#d92d20',
  error700: '#b42318',

  // Warning
  warning50: '#fffaeb',
  warning500: '#f79009',
  warning600: '#dc6803',

  // Success
  success50: '#ecfdf3',
  success500: '#17b26a',
  success600: '#079455',

  // Info
  info50: '#eff4ff',
  info500: '#2970ff',
  info600: '#155eef',
}

export const typography = {
  fontFamily: 'Satoshi, sans-serif',
  fontFamilyMedium: 'SatoshiMedium, sans-serif',
  fontFamilyRegular: 'SatoshiRegular, sans-serif',

  weightRegular: 400,
  weightMedium: 500,
  weightSemibold: 600,
  weightBold: 700,

  sizeXs: 12,
  sizeSm: 14,
  sizeMd: 16,
  sizeLg: 18,
  sizeXl: 20,
}

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  '3xl': 24,
  '4xl': 32,
  '5xl': 40,
}

export const radius = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  '2xl': 16,
  '3xl': 20,
  full: 9999,
}

export const shadows = {
  xs: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  sm: '0px 1px 2px 0px rgba(10, 13, 18, 0.06), 0px 1px 3px 0px rgba(10, 13, 18, 0.1)',
  md: '0px 2px 4px -2px rgba(10, 13, 18, 0.06), 0px 4px 8px -2px rgba(10, 13, 18, 0.1)',
  lg: '0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 12px 16px -4px rgba(10, 13, 18, 0.08)',
  focusRing: '0px 0px 0px 4px rgba(104, 82, 214, 0.24)',
  focusRingError: '0px 0px 0px 4px rgba(240, 68, 56, 0.24)',
}

// ============================================================
// ANTD CONFIGPROVIDER THEME
// ============================================================

export const antdTheme = {
  token: {
    colorPrimary: colors.primary,
    colorError: colors.error500,
    colorSuccess: colors.success500,
    colorWarning: colors.warning500,
    colorInfo: colors.info500,
    borderRadius: radius.md,
    fontFamily: typography.fontFamily,
  },
}

// Default export for backward compatibility
export default antdTheme
